import { useCallback, useEffect, useState } from 'react'

/**
 * App-wide text/zoom control. Bound to Cmd/Ctrl + (=/+) to enlarge, Cmd/Ctrl +
 * (-/_) to shrink, and Cmd/Ctrl + 0 to reset, and — as of VRG-248 — also driven
 * by the Text size picker on Settings → Personal → Appearance.
 *
 * Applies a `zoom` factor to the app's mount root (`#app`, NOT `document.body`
 * — see "Why `#app` and not `document.body`" below) — uniform scaling that
 * works with the codebase's px-based styling — and persists it to localStorage
 * so it survives reloads.
 *
 * We intercept these chords (preventDefault) so the in-app scale replaces the
 * browser's native zoom rather than stacking with it.
 *
 * ## Why `zoom` and not a root font-size with rem units
 *
 * The Appearance copy promises this scales "the whole interface (text, spacing,
 * and controls) ... like a browser zoom". A root-font-size approach delivers
 * that only if the app's geometry is in rem. This one is not, and could not
 * cheaply be made so: `packages/ui/theme/spacing.ts` is
 * `(factor) => `${4 * factor}px``, so every `spacing={n}` and the `p: 6` page
 * gutter are literal px; SIDEBAR_WIDTH (256), SIDEBAR_MINIMIZED_WIDTH (80),
 * SIDEBAR_HEADER_HEIGHT (88), the 48px sidebar rows and the Table's `topOffset`
 * are plain numbers. A root-font-size change would grow the text and leave the
 * sidebar, tables and gutters exactly where they were — which reads as broken,
 * not as scaled. `zoom` scales the rendered result of all of it for free.
 *
 * ## Why `#app` and not `document.body` (VRG-227)
 *
 * Every MUI popover/menu/tooltip/dropdown positions itself in TWO steps that
 * must land in the SAME coordinate space: (1) read the trigger's real,
 * post-zoom position via `anchorEl.getBoundingClientRect()`, then (2) write
 * that value back as a literal CSS length (`style.top`/`style.left`, or
 * `transform: translate3d(x, y, 0)` for Popper.js-based components — Tooltip,
 * Autocomplete, MUI-X date pickers) on the popover's own DOM node. `zoom`
 * rescales EVERY explicit CSS length declared within its subtree, including an
 * element's own position offset — so if that offset-carrying node sits ANYWHERE
 * inside a zoomed subtree (itself, or the default portal target it renders
 * into), the already-real-pixel value from step 1 gets multiplied by the zoom
 * factor AGAIN in step 2. Confirmed empirically (isolated repro, zero app
 * dependency): the resulting position error is EXACTLY `intendedPosition ×
 * (zoom − 1)` at every tested level (0.9/1/1.2/1.4) — which is also why the
 * error visibly grows for anchors further down a long page (`intendedPosition`
 * grows with scroll depth) and why it reproduced identically whether the zoom
 * lived on `<body>`, a portal-root sibling with a matching zoom value, or the
 * popover's own paper — ANY zoomed ancestor (or self) between the offset and
 * the true unzoomed viewport root causes it.
 *
 * `document.body` is the universal fallback portal container MUI resolves to
 * when no explicit `container` is given (`Popover.js`, `BasePopper.js`,
 * `Portal.js` all fall back to `ownerDocument(...).body` / `document.body`) —
 * confirmed by reading MUI's source, not assumed. `apps/platform/index.html`
 * has exactly one child of `<body>`: `<div id="app">`, which is the entire
 * React-rendered subtree. So mounting the zoom on `#app` instead of `body`
 * changes NOTHING about the app's own rendering (100% the same subtree) while
 * making `document.body` — and therefore every popover/menu/tooltip/dropdown/
 * date-picker portal that defaults into it — permanently outside the zoomed
 * coordinate space. That is the fix: it is not a per-component patch, it is
 * removing the one shared ancestor that was causing every portal to compound
 * its own already-correct anchor math.
 *
 * Popovers/menus still need to visually SCALE with zoom (the whole point of
 * this control) — that is restored surgically, per component family, by
 * applying `zoom: var(--text-zoom)` to a DESCENDANT of the position-carrying
 * node (never the node itself, never an ancestor of it) — see
 * `packages/ui/theme/theme.ts`'s `MuiPopover`/`MuiTooltip`/`MuiDrawer`/
 * `MuiDialog`/`MuiPickersPopper` overrides, and `SearchOverlay.tsx` for the
 * one hand-built ⌘K palette that isn't a MUI portal primitive.
 *
 * ## The `--text-zoom` custom property
 *
 * `vh` units resolve against the real viewport and are THEN scaled by the zoom,
 * so a `height: 100vh` box inside `body { zoom: 1.2 }` paints 20% taller than
 * the window. The factor is therefore mirrored onto <html> (outside the zoomed
 * subtree) so full-height containers can divide it back out. Always write these
 * as `vhMinus(...)` from `utils/textZoom` rather than hand-rolling the calc.
 *
 * ## Per-device, not per-profile
 *
 * localStorage, like the sidebar display mode: the right scale on a 13" laptop
 * is not the right one on a 27" monitor, so syncing it across a user's devices
 * would fight them. Theme is the deliberate contrast — it IS on the profile,
 * because "I prefer dark" should follow the person.
 */
const STORAGE_KEY = 'vera-text-zoom'
const MIN = 0.7
const MAX = 1.8
const STEP = 0.1
export const DEFAULT_TEXT_ZOOM = 1

/**
 * Same-tab change notification. `applyZoom` writes straight to the DOM, so
 * nothing re-renders on its own — without this the Appearance picker would
 * resize the app on Cmd + and then keep highlighting the previous option. The
 * cross-tab case is covered by the native `storage` event.
 */
export const TEXT_ZOOM_CHANGE_EVENT = 'vera:text-zoom-changed'

/**
 * The four factors the Appearance picker offers, chosen off the chords' own
 * 0.7–1.8 range. Two constraints drove the choice:
 *
 *   - Each lands exactly on the 0.1 chord grid, so stepping with Cmd +/- from a
 *     picker value stays on-grid and the picker keeps highlighting a real option
 *     rather than falling to "nothing selected" after one keypress.
 *   - They span a useful spread of the range instead of crowding its bottom
 *     fifth. 1.4 is a genuine "extra large" (roughly a browser 140% zoom); a
 *     1.2 ceiling would have made the top option barely distinguishable from
 *     Large, which is the complaint the setting exists to answer.
 *
 * The chords keep the FULL 0.7–1.8 continuous range — the picker is four
 * shortcuts into it, not a replacement for it. A user who chords to 1.1 sees no
 * option highlighted, which is honest: none of them is what is applied.
 */
export type TextZoomLevel = 0.9 | 1 | 1.2 | 1.4

export const TEXT_ZOOM_LEVELS: readonly TextZoomLevel[] = [0.9, 1, 1.2, 1.4]

export const TEXT_ZOOM_LABELS: Record<TextZoomLevel, string> = {
  0.9: 'Compact',
  1: 'Default',
  1.2: 'Large',
  1.4: 'Extra large',
}

const clampRound = (z: number) => Math.min(MAX, Math.max(MIN, Math.round(z * 10) / 10))

/**
 * The app's mount root — the ONE child of `document.body`
 * (`apps/platform/index.html`) — not `document.body` itself. See "Why `#app`
 * and not `document.body`" above: `body` must stay the permanently-unzoomed
 * ancestor every MUI popover/menu/tooltip/date-picker portal defaults into,
 * or their own already-correct anchor-position math gets rescaled a second
 * time by the same zoom. Falls back to `document.body` defensively (e.g. a
 * test environment that never mounted the real index.html), which reproduces
 * the pre-fix behavior rather than silently no-op'ing.
 */
const getZoomTarget = (): HTMLElement => document.getElementById('app') || document.body

/**
 * Paint + persist. Kept byte-compatible with the pre-VRG-248 format (the raw
 * factor, stringified, under the same key) so nothing needs migrating and any
 * other reader of the key keeps working.
 */
const applyZoom = (z: number) => {
  ;(getZoomTarget().style as any).zoom = String(z)
  // Expose the factor so full-height containers can compensate: a `height:100vh`
  // element inside a `zoom:z` app root only fills z×100vh visually, leaving a gap
  // (the "black band"). `calc(100vh / var(--text-zoom))` restores full fill.
  document.documentElement.style.setProperty('--text-zoom', String(z))
  try {
    localStorage.setItem(STORAGE_KEY, String(z))
  } catch {
    /* ignore storage failures (private mode, etc.) */
  }
}

export const readTextZoom = (): number => {
  if (typeof window === 'undefined') return DEFAULT_TEXT_ZOOM
  try {
    return clampRound(parseFloat(localStorage.getItem(STORAGE_KEY) || '1') || 1)
  } catch {
    return DEFAULT_TEXT_ZOOM
  }
}

/** The single entry point for CHANGING the zoom — paint, persist, announce. */
export const setTextZoom = (z: number) => {
  const next = clampRound(z)
  applyZoom(next)
  window.dispatchEvent(new CustomEvent(TEXT_ZOOM_CHANGE_EVENT))
  return next
}

/**
 * Mounted ONCE at the app root: applies the stored zoom on load and installs
 * the keyboard chords.
 */
export const useTextZoom = () => {
  useEffect(() => {
    let zoom = readTextZoom()
    applyZoom(zoom)

    const onKeyDown = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.altKey) return
      const k = e.key
      if (k === '=' || k === '+') {
        e.preventDefault()
        zoom = setTextZoom(zoom + STEP)
      } else if (k === '-' || k === '_') {
        e.preventDefault()
        zoom = setTextZoom(zoom - STEP)
      } else if (k === '0') {
        e.preventDefault()
        zoom = setTextZoom(DEFAULT_TEXT_ZOOM)
      }
    }

    // Keep the local cursor honest when the Appearance picker (or another tab)
    // changes it, so the next Cmd + steps from what is actually applied.
    const sync = () => {
      zoom = readTextZoom()
    }

    window.addEventListener('keydown', onKeyDown)
    window.addEventListener(TEXT_ZOOM_CHANGE_EVENT, sync)
    window.addEventListener('storage', sync)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener(TEXT_ZOOM_CHANGE_EVENT, sync)
      window.removeEventListener('storage', sync)
    }
  }, [])
}

/**
 * Read/write the zoom from UI (the Appearance Text size picker).
 *
 * Stays in sync with the chords and with other tabs by listening to the
 * same-tab CustomEvent and the cross-tab `storage` event.
 */
export const useTextZoomFactor = (): { factor: number; setFactor: (z: number) => void } => {
  const [factor, setFactorState] = useState<number>(readTextZoom)

  useEffect(() => {
    const sync = (event: Event) => {
      if (event instanceof StorageEvent && event.key && event.key !== STORAGE_KEY) return
      const next = readTextZoom()
      setFactorState(next)
      // A cross-tab change is persisted but not yet painted in THIS tab.
      if (event instanceof StorageEvent) applyZoom(next)
    }

    window.addEventListener(TEXT_ZOOM_CHANGE_EVENT, sync)
    window.addEventListener('storage', sync)
    return () => {
      window.removeEventListener(TEXT_ZOOM_CHANGE_EVENT, sync)
      window.removeEventListener('storage', sync)
    }
  }, [])

  const setFactor = useCallback((z: number) => {
    setFactorState(setTextZoom(z))
  }, [])

  return { factor, setFactor }
}
